import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { HealthService } from '../../core/services/health.service';

/** Seconds between automatic health checks while the backend is down. */
const AUTO_RETRY_SECONDS = 30;

/**
 * Maintenance screen shown when the backend is unavailable (Stela 2 design, Mantenimiento.html
 * in Claude Design): brand panel shared with the login + status card with a retry countdown.
 *
 * Texts are hardcoded in Spanish on purpose: translations are served by the API, which is
 * exactly what is down when this screen is visible.
 */
@Component({
    selector: 'app-maintenance',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
<div class="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-(--stela-surface) text-gray-800">
    <!-- Brand panel (same as the login) -->
    <aside class="relative overflow-hidden flex flex-col justify-between gap-8 lg:gap-0 p-8 lg:p-12 text-white bg-[linear-gradient(160deg,#3E2C42_0%,#1F1422_100%)]">
        <div class="absolute inset-0 opacity-[0.08] pointer-events-none bg-[radial-gradient(circle_at_20%_30%,#D4956A_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#D4956A_0%,transparent_40%)]"></div>
        <img src="/assets/img/Stela_logo_horizontal.png" class="relative h-14 w-auto max-w-full self-start object-contain" alt="Stela · Protocolo & Eventos">
        <div class="relative max-w-[480px]">
            <h2 class="m-0 text-[30px] lg:text-[44px] leading-[1.15] font-medium" style="font-family: var(--stela-font-display)">Volvemos en breve.</h2>
            <p class="hidden lg:block mt-[18px] text-base leading-relaxed opacity-75">
                Estamos realizando tareas de mantenimiento para que Stela siga a la altura de cada acto.
            </p>
        </div>
        <div class="relative text-[11px] opacity-50 tracking-[0.8px] uppercase">&copy; Diaple Networking SL {{ currentYear }}</div>
    </aside>

    <!-- Status pane -->
    <main class="flex items-center justify-center p-6 sm:p-12">
        <div class="w-full max-w-[400px] flex flex-col gap-6">
            <div>
                <span class="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase text-[#8A6D1F]">
                    <i class="w-2 h-2 rounded-full bg-gold shadow-[0_0_0_4px_rgba(201,168,76,0.2)]"></i>
                    Mantenimiento en curso
                </span>
                <h1 class="mt-2.5 mb-2 text-[32px] leading-tight font-medium text-gray-800" style="font-family: var(--stela-font-display)">
                    El sistema no está disponible
                </h1>
                <p class="m-0 text-[15px] leading-relaxed text-gray-500">
                    Estamos trabajando para restablecer el servicio lo antes posible. Tus datos están a salvo; no es necesario que hagas nada.
                </p>
            </div>

            <div class="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 px-4 py-3.5 border border-gray-200 rounded-md text-[13px]" role="status" aria-live="polite">
                @if (isRetrying()) {
                    <i class="w-2 h-2 rounded-full bg-gold animate-pulse"></i>
                    <b class="font-semibold text-gray-800">Comprobando estado del servidor…</b>
                } @else {
                    <i class="w-2 h-2 rounded-full bg-[#B42318]"></i>
                    <b class="font-semibold text-gray-800">{{ healthService.status().message || 'No se ha podido conectar con el servidor' }}</b>
                    <span class="col-start-2 text-xs text-gray-500">
                        Última verificación: {{ healthService.status().checkedAt | date:'HH:mm:ss' }} · reintento automático en {{ secondsLeft() }} s
                    </span>
                }
            </div>

            <button type="button"
                    class="w-full h-11 inline-flex items-center justify-center gap-2 bg-(--stela-gold) hover:bg-(--stela-gold-dark) text-(--stela-aubergine-dark) text-sm font-semibold rounded-md border-0 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-default"
                    [disabled]="isRetrying()"
                    (click)="onRetry()">
                <svg viewBox="0 0 24 24" class="w-4 h-4" [class.animate-spin]="isRetrying()" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path>
                    <path d="M3.5 9a9 9 0 0 1 14.8-3.4L23 10M1 14l4.7 4.4A9 9 0 0 0 20.5 15"></path>
                </svg>
                <span>Reintentar conexión</span>
            </button>

            <div class="pt-5 border-t border-gray-200 text-[13px] leading-relaxed text-gray-500">
                Si el problema persiste, contacta con soporte:
                <a href="mailto:soporte@diaple.com" class="text-[#8A6D1F] hover:text-[#6B5416]">soporte&#64;diaple.com</a>
            </div>
        </div>
    </main>
</div>
    `,
    imports: [DatePipe]
})
export class MaintenanceComponent {
    protected healthService = inject(HealthService);
    private readonly destroyRef = inject(DestroyRef);

    /** Event emitted when retry succeeds and backend is healthy */
    readonly retrySuccess = output<void>();

    /** Current year for copyright footer */
    readonly currentYear = new Date().getFullYear();

    readonly isRetrying = signal(false);
    readonly secondsLeft = signal(AUTO_RETRY_SECONDS);

    constructor() {
        interval(1_000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isRetrying()) {
                    return;
                }
                const next = this.secondsLeft() - 1;
                if (next <= 0) {
                    void this.onRetry();
                } else {
                    this.secondsLeft.set(next);
                }
            });
    }

    async onRetry(): Promise<void> {
        this.isRetrying.set(true);

        try {
            const status = await this.healthService.checkHealth();

            if (status.isHealthy) {
                this.retrySuccess.emit();
            }
        } finally {
            this.isRetrying.set(false);
            this.secondsLeft.set(AUTO_RETRY_SECONDS);
        }
    }
}
